import React, { useEffect, useState } from 'react';
import { useGetList, useTranslate } from 'react-admin';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

export const LOADS = ['low', 'normal', 'high', 'overloaded'];
export const PRIORITIES = ['high', 'medium', 'low'];
export const STATUSES = ['active', 'paused', 'done'];

const gridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, marginTop: '8px' };

// The Kernel theme caps full width fields at 50%: dialogs need the whole width.
const contentSx = { '& .MuiFormControl-fullWidth': { maxWidth: '100%' }, '& .MuiFormControl-root': { minWidth: '0 !important' } };

const numberOrNull = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

// Edit the manager fields of a card: headline, capacity, perceived load, note.
export const CardDialog = ({ card, open, onClose, onSave }) => {
    const translate = useTranslate();
    const [form, setForm] = useState({});

    useEffect(() => {
        if (open) {
            setForm({
                headline: card.headline || '',
                capacity_days_week: card.capacity_days_week ?? 5,
                manager_load: card.manager_load || '',
                manager_note: card.manager_note || '',
            });
        }
    }, [open, card]);

    const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{card.user.fullname}</DialogTitle>
            <DialogContent sx={contentSx}>
                <TextField fullWidth label={translate('Headline')} helperText={translate('For example: Odoo + web')}
                    value={form.headline || ''} onChange={set('headline')} sx={{ marginTop: '8px' }} />
                <Box sx={gridSx}>
                    <TextField type="number" label={translate('Capacity (days / week)')} inputProps={{ step: 0.5, min: 0, max: 7 }}
                        value={form.capacity_days_week ?? ''} onChange={set('capacity_days_week')} />
                    <TextField select label={translate('My perception of the load')} value={form.manager_load || ''} onChange={set('manager_load')}>
                        <MenuItem value="">-</MenuItem>
                        {LOADS.map(load => <MenuItem key={load} value={load}>{translate(`focus_load.${load}`, { _: load })}</MenuItem>)}
                    </TextField>
                </Box>
                <TextField fullWidth multiline minRows={3} label={translate('Note')} value={form.manager_note || ''}
                    onChange={set('manager_note')} sx={{ marginTop: '16px' }} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{translate('ra.action.cancel')}</Button>
                <Button variant="contained" onClick={() => onSave({
                    ...form,
                    capacity_days_week: numberOrNull(form.capacity_days_week),
                    manager_load: form.manager_load || null,
                })}>{translate('ra.action.save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

// Create or edit one priority, with its effort and the projects it is worked on.
export const ItemDialog = ({ item, open, onClose, onSave, suggestions = [] }) => {
    const translate = useTranslate();
    const [form, setForm] = useState({});
    const { data: projects = [] } = useGetList('projects', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'name', order: 'ASC' },
    }, { enabled: open });

    useEffect(() => {
        if (open) {
            setForm({
                title: item?.title || '',
                description: item?.description || '',
                priority: item?.priority || 'medium',
                status: item?.status || 'active',
                effort_total_days: item?.effort_total_days ?? '',
                effort_week_days: item?.effort_week_days ?? '',
                start_date: item?.start_date || '',
                end_date: item?.end_date || '',
                project_ids: item?.project_ids || [],
            });
        }
    }, [open, item]);

    const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });
    const selectedProjects = projects.filter(project => (form.project_ids || []).includes(project.id));

    const save = () => onSave({
        ...form,
        effort_total_days: numberOrNull(form.effort_total_days),
        effort_week_days: numberOrNull(form.effort_week_days),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
    });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{item?.id ? translate('Edit the priority') : translate('New priority')}</DialogTitle>
            <DialogContent sx={contentSx}>
                <TextField fullWidth autoFocus label={translate('Title')} value={form.title || ''} onChange={set('title')} sx={{ marginTop: '8px' }} />
                <TextField fullWidth multiline minRows={2} label={translate('Description')} value={form.description || ''}
                    onChange={set('description')} sx={{ marginTop: '16px' }} />
                <Box sx={gridSx}>
                    <TextField select label={translate('Priority')} value={form.priority || 'medium'} onChange={set('priority')}>
                        {PRIORITIES.map(value => <MenuItem key={value} value={value}>{translate(`focus_priority.${value}`, { _: value })}</MenuItem>)}
                    </TextField>
                    <TextField select label={translate('Status')} value={form.status || 'active'} onChange={set('status')}>
                        {STATUSES.map(value => <MenuItem key={value} value={value}>{translate(`focus_status.${value}`, { _: value })}</MenuItem>)}
                    </TextField>
                    <TextField type="number" label={translate('Total effort (days)')} inputProps={{ step: 0.5, min: 0 }}
                        value={form.effort_total_days ?? ''} onChange={set('effort_total_days')} />
                    <TextField type="number" label={translate('Effort per week (days)')} inputProps={{ step: 0.5, min: 0, max: 7 }}
                        value={form.effort_week_days ?? ''} onChange={set('effort_week_days')} />
                    <TextField type="date" label={translate('From')} InputLabelProps={{ shrink: true }}
                        value={form.start_date || ''} onChange={set('start_date')} />
                    <TextField type="date" label={translate('To')} InputLabelProps={{ shrink: true }}
                        value={form.end_date || ''} onChange={set('end_date')} />
                </Box>
                <Autocomplete
                    multiple
                    options={projects}
                    value={selectedProjects}
                    getOptionLabel={(project) => project.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(event, value) => setForm({ ...form, project_ids: value.map(project => project.id) })}
                    renderInput={(params) => (
                        <TextField {...params} label={translate('Linked projects')}
                            helperText={translate('The time logged on these projects counts as effort spent.')} />
                    )}
                    sx={{ marginTop: '16px' }}
                />
                {suggestions.filter(project => project.project_id && !(form.project_ids || []).includes(project.project_id)).length > 0 && (
                    <Box sx={{ marginTop: '10px' }}>
                        <Typography variant="caption" color="text.secondary">{translate('Where this person spent time lately:')}</Typography>
                        <Box display="flex" gap={0.75} flexWrap="wrap" sx={{ marginTop: '4px' }}>
                            {suggestions.filter(project => project.project_id && !(form.project_ids || []).includes(project.project_id)).map(project => (
                                <Button key={project.project_id} size="small" variant="outlined" sx={{ textTransform: 'none', borderRadius: '12px', paddingY: 0 }}
                                    onClick={() => setForm({ ...form, project_ids: [...(form.project_ids || []), project.project_id] })}>
                                    + {project.name} ({project.pct} %)
                                </Button>
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{translate('ra.action.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={!form.title?.trim()}>{translate('ra.action.save')}</Button>
            </DialogActions>
        </Dialog>
    );
};
